/**
 * Judgment Index CSV import — matches the real score sheet structure.
 *
 * Parses a practitioner's spreadsheet of Judgment Index results into profile
 * inputs. Pure and testable — no I/O. The store handles persistence.
 *
 * Required columns (order-independent, case-insensitive):
 *   name
 *   p1_intrinsic  (or: people, relational)
 *   p1_extrinsic  (or: work_task, tactical)
 *   p1_systemic   (or: big_picture, strategic)
 *   p2_intrinsic  (or: self_intrinsic, self_worth)
 *   p2_extrinsic  (or: self_extrinsic, role_clarity)
 *   p2_systemic   (or: self_systemic, self_direction)
 *
 * Optional columns:
 *   email, assessed_on (or: assessed)
 *   bal_people, bal_work, bal_ideas, bal_esteem, bal_concept, bal_image
 *   p1_intrinsic_band, p1_extrinsic_band, p1_systemic_band   (Part 1 band labels)
 *   p2_intrinsic_band, p2_extrinsic_band, p2_systemic_band   (Part 2 band labels)
 *   judgment_type, judgment_type_name (or: type, type_name)
 *   notes
 */

import type { JudgmentProfileInput, JudgmentIES, JudgmentIESBands, JudgmentBalance, ScoreBand } from '../types/prioritization/judgmentIndex';

function parseBandLabel(s: string): ScoreBand | null {
  const c = s.trim().toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, ' ');
  if (c === 'strong' || c === 's') return 'strong';
  if (c === 'good' || c === 'g') return 'good';
  if (c.includes('significant') || c === 'nsd') return 'needs_work';
  if (c.includes('development') || c === 'nd') return 'developing';
  if (c.includes('attention') || c === 'na') return 'attention';
  return null;
}

export interface CsvParseResult {
  rows: JudgmentProfileInput[];
  errors: string[];
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function toNum(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw.trim());
  return Number.isNaN(n) ? null : n;
}

function toIES(cells: string[], col: (names: string[]) => number): JudgmentIES {
  const get = (names: string[]): number => {
    const idx = col(names);
    return idx !== -1 ? (toNum(cells[idx] ?? '') ?? 0) : 0;
  };
  return {
    intrinsic: get(['p1_intrinsic', 'people', 'relational']),
    extrinsic: get(['p1_extrinsic', 'work_task', 'tactical']),
    systemic:  get(['p1_systemic', 'big_picture', 'strategic']),
  };
}

function toSelfIES(cells: string[], col: (names: string[]) => number): JudgmentIES {
  const get = (names: string[]): number => {
    const idx = col(names);
    return idx !== -1 ? (toNum(cells[idx] ?? '') ?? 0) : 0;
  };
  return {
    intrinsic: get(['p2_intrinsic', 'self_intrinsic', 'self_worth']),
    extrinsic: get(['p2_extrinsic', 'self_extrinsic', 'role_clarity']),
    systemic:  get(['p2_systemic',  'self_systemic',  'self_direction']),
  };
}

function toBalance(cells: string[], col: (names: string[]) => number): JudgmentBalance {
  const get = (names: string[], fallback = 0): number => {
    const idx = col(names);
    return idx !== -1 ? (toNum(cells[idx] ?? '') ?? fallback) : fallback;
  };
  return {
    people:  get(['bal_people',  'balance_people',  'people_balance']),
    work:    get(['bal_work',    'balance_work',    'work_balance']),
    ideas:   get(['bal_ideas',   'balance_ideas',   'ideas_balance']),
    esteem:  get(['bal_esteem',  'balance_esteem',  'esteem_balance']),
    concept: get(['bal_concept', 'balance_concept', 'concept_balance']),
    image:   get(['bal_image',   'balance_image',   'image_balance']),
  };
}

const WORK_SIDE_COLUMNS = ['p1_intrinsic', 'people', 'relational', 'p1_extrinsic', 'work_task', 'tactical', 'p1_systemic', 'big_picture', 'strategic'];
const SELF_SIDE_COLUMNS = ['p2_intrinsic', 'self_intrinsic', 'self_worth', 'p2_extrinsic', 'self_extrinsic', 'role_clarity', 'p2_systemic', 'self_systemic', 'self_direction'];

export function parseJudgmentCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);

  if (lines.length < 2) {
    return { rows: [], errors: ['The file needs a header row and at least one data row.'] };
  }

  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
  const col = (names: string[]): number => {
    for (const name of names) {
      const idx = header.indexOf(name.toLowerCase());
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const nameIdx = col(['name']);
  if (nameIdx === -1) {
    return { rows: [], errors: ['Missing required "name" column in header.'] };
  }

  const hasWorkSide = WORK_SIDE_COLUMNS.some((c) => header.includes(c));
  const hasSelfSide = SELF_SIDE_COLUMNS.some((c) => header.includes(c));
  if (!hasWorkSide && !hasSelfSide) {
    return {
      rows: [],
      errors: [
        'No IES score columns found. Expected columns like: p1_intrinsic, p1_extrinsic, p1_systemic, p2_intrinsic, p2_extrinsic, p2_systemic.',
      ],
    };
  }

  const emailIdx     = col(['email']);
  const dateIdx      = col(['assessed_on', 'assessed', 'date']);
  const typeIdx      = col(['judgment_type', 'type', 'primary_type']);
  const typeNameIdx  = col(['judgment_type_name', 'type_name', 'primary_type_name']);
  const notesIdx     = col(['notes', 'note']);
  const p1IBandIdx   = col(['p1_intrinsic_band', 'p1_i_band', 'work_people_band']);
  const p1EBandIdx   = col(['p1_extrinsic_band', 'p1_e_band', 'work_tactical_band']);
  const p1SBandIdx   = col(['p1_systemic_band',  'p1_s_band', 'work_strategic_band']);
  const p2IBandIdx   = col(['p2_intrinsic_band', 'p2_i_band', 'self_worth_band']);
  const p2EBandIdx   = col(['p2_extrinsic_band', 'p2_e_band', 'role_clarity_band']);
  const p2SBandIdx   = col(['p2_systemic_band',  'p2_s_band', 'self_direction_band']);

  const rows: JudgmentProfileInput[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i]);
    const name = (cells[nameIdx] ?? '').trim();
    if (!name) {
      errors.push(`Row ${i + 1}: skipped — no name.`);
      continue;
    }

    const rawType = typeIdx !== -1 ? toNum(cells[typeIdx] ?? '') : null;

    const getBand = (idx: number): ScoreBand | null =>
      idx !== -1 ? parseBandLabel(cells[idx] ?? '') : null;

    const workSideBands: JudgmentIESBands = {
      intrinsic: getBand(p1IBandIdx),
      extrinsic: getBand(p1EBandIdx),
      systemic:  getBand(p1SBandIdx),
    };
    const selfSideBands: JudgmentIESBands = {
      intrinsic: getBand(p2IBandIdx),
      extrinsic: getBand(p2EBandIdx),
      systemic:  getBand(p2SBandIdx),
    };
    const hasWorkBands = workSideBands.intrinsic !== null || workSideBands.extrinsic !== null || workSideBands.systemic !== null;
    const hasSelfBands = selfSideBands.intrinsic !== null || selfSideBands.extrinsic !== null || selfSideBands.systemic !== null;

    rows.push({
      subjectName: name,
      subjectEmail: emailIdx !== -1 ? (cells[emailIdx] ?? '').trim() || null : null,
      assessedOn:   dateIdx  !== -1 ? (cells[dateIdx]  ?? '').trim() || null : null,
      source: 'upload',
      workSide: toIES(cells, col),
      selfSide: toSelfIES(cells, col),
      balance:  toBalance(cells, col),
      workSideBands: hasWorkBands ? workSideBands : null,
      selfSideBands: hasSelfBands ? selfSideBands : null,
      judgmentType:     rawType,
      judgmentTypeName: typeNameIdx !== -1 ? (cells[typeNameIdx] ?? '').trim() || null : null,
      notes: notesIdx !== -1 ? (cells[notesIdx] ?? '').trim() || null : null,
    });
  }

  if (rows.length === 0) errors.push('No valid data rows found.');
  return { rows, errors };
}
